// src/components/BattleForMuniContent.js
import React, { useState, useEffect, useRef } from 'react';
import { Avatar, Accordion, AccordionSummary, AccordionDetails, Card, CardContent, CardActions, Grid, Button, Paper, Box, Typography, Divider, List, ListItem, ListItemIcon, ListItemText, TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/lab';
import EmeraldMapSVG from './EmeraldMapSVG';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { IconButton } from '@mui/material';

const ruleTitles = [
  'Claiming Holes',
  'Birdie Power',
  'Par Steal',
  'Losing Territory',
  'Land Rush',
  'Winning'
];

const prizeTitles = [
  'First Claim',
  'Nine Claimed',
  'Course Claimed'
];

const rules = [
  'All holes are open to start. A Par or Birdie will claim an unclaimed hole.',
  'Birdies always take over a claimed hole, no matter who owns it.',
  'Once a round, you may use a Par to steal a hole, claimed with a Birdie.',
  'If you score double the value of a hole (+4 on a Par 4), it becomes unclaimed.',
  'If you claim 4 holes in one day add any extra hole of your choice to your total.',
  'First to claim all 18 holes wins the battle.'
];

const prizes = [
  'First hole claimed is worth 1 Beer',
  'First to 9 holes, free round + 1 Beer',
  'All 18 claimed, King of Muni T-shirt, 12 pack of Beer',
];

const holes = Array.from({ length: 18 }, (_, i) => i + 1);

function getColorFromKey(key) {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = key.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = hash % 360;
  return `hsl(${hue}, 60%, 85%)`;
}

export default function BattleForEmeraldContent({ onShowScoreForm, territories, parByHoleEm, timeline, onRuleTap = () => {}}) {
  const [showRules, setShowRules] = useState(false);
  const [showPrizes, setShowPrizes] = useState(false);
  const [revealed, setRevealed] = useState([]);
  const [expandedHole, setExpandedHole] = useState(null);


  useEffect(() => {
    let i = 0;
    const ids = Array.from({ length: 18 }, (_, idx) => String(idx + 1));
    const interval = setInterval(() => {
      setRevealed(prev => [...prev, ids[i]]);
      i++;
      if (i >= ids.length) clearInterval(interval);
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const players = [...new Set(territories.map(t => t.battleKey))];

  return (
    <Grid container spacing={4}>
      <Grid container spacing={4} sx={{ width: '100%' }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: 2, width: '100%', background: 'white', border: '5px solid #106a41', mb:0}}>
          <img
            src="https://images.ctfassets.net/zw84cuagwed8/3Zjes9gDWmZlu2tfnpcHdc/dc2f9041f1ba57202dc8dc84f3f39441/dc546555-c904-49b5-9c76-069bdbfe8cf7.png"
            alt="Battle for Emerald"
            style={{
              maxWidth: '300px',
              height: 'auto',
              display: 'block',
              marginBottom: '1rem',
              marginLeft: '-5px'
            }}
          />
          <Typography variant="subtitle1" color="text.secondary" paragraph sx={{border: 0, fontWeight: 600}}>
            Take over the great Emerald Isle one hole at a time… Who will be the King of the Isle?
          </Typography>

          <EmeraldMapSVG territories={territories} revealed={revealed} />
          {/* Territory Table */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              flexWrap: 'wrap',
              gap: players.length < 1 ? 0 : 2,
              mb: players.length < 1 ? 0 : 4,
              mt: players.length < 1 ? 0 : 2
            }}
          >
            {players
              .map(key => ({
                key,
                claims: territories.filter(t => t.battleKey === key && !t.isBlown),
              }))
              .filter(player => player.claims.length > 0) 
              .sort((a, b) => b.claims.length - a.claims.length)
              .map(({ key, claims }) => (
                <Box
                  key={key}
                  sx={{
                    flex: '1 1 calc(33.333% - 16px)',
                    minWidth: 200,
                    ...(players.length <= 2 && { maxWidth: 250 }),
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  <Card className="player-card" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                      <Box
                        className={`bk-${key} avatar`}
                        sx={{ width: 50, height: 50, borderRadius: '50%', mr: 2 }}
                      />
                      <Box>
                        <Typography variant="h6">{key}</Typography>
                        <Typography sx={{ color: 'text.secondary' }}>
                          {claims.length} hole{claims.length > 1 ? 's' : ''} claimed
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
            ))}
          </Box>
          <Typography variant="h6"sx={{borderBottom: '0px solid #cccccc', mb: 1}} gutterBottom>Territory</Typography>
          <TableContainer id="em-table" sx={{ width: '100%', overflowX: 'hidden' }}>
            <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell align="left" sx={{ fontWeight: 600, width: '13%' }}>
                    Hole
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, width: '22.5%' }}>
                    Claim
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, width: '22.5%' }}>
                    Score
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, width: '15%' }}>
                    Par
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, width: '30%' }}>
                    Player
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {holes.map(n => {
                  const match = territories.find(t => t.territory === String(n));
                  const par = parByHoleEm[n - 1];
                  const scoreDisplay = match
                  ? match.isBlown
                    ? '-'
                    : Number(match.score) === par - 2
                      ? '🐦'
                      : Number(match.score) === par - 1
                        ? '🕊️'
                        : Number(match.score) === par
                          ? '🚩'
                          : match.score
                  : '–';

                  const rowClass = match
                  ? `bk-${match.battleKey} ${match.isDuplicate ? 'taken' : ''} ${match.isBlown ? 'blown' : ''}`
                  : '';

                  return (
                    <React.Fragment key={n}>
                    <TableRow key={n} className={rowClass}>
                      <TableCell align="center">{n}</TableCell>
                      <TableCell align="center">
                        {match && !match.isBlown ? '' : 'No'}
                      </TableCell>
                      <TableCell align="center">{scoreDisplay}</TableCell>
                      <TableCell align="center">{par}</TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          zIndex: 100,
                          position: 'relative'
                        }}
                      >
                      <Box
                        sx={{
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          ...(timeline[String(n)]?.length > 1 && {
                            '@media (max-width: 767px)': {
                              paddingRight: 2,
                            },
                          }),
                        }}
                      >
                        {match && !match.isBlown ? match.battleKey : '–'}
                          {timeline[String(n)]?.length > 1 && (
                            <IconButton
                              size="small"
                              onClick={() =>
                                setExpandedHole(expandedHole === n ? null : n)
                              }
                              sx={{
                                ml: 1,
                                height: '20px',
                                width: '20px',
                                position: 'absolute',
                                right: '5px',
                                border: '0 !important',
                                backgroundColor: 'white',
                                '&:hover': {
                                  backgroundColor: '#ddd', // slightly darker on hover
                                },
                              }}
                            >
                              <ExpandMoreIcon
                                sx={{
                                  transform: expandedHole === n ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.2s ease',
                                  width: '20px',
                                  height: '20px',
                                  color: '#000',
                                  opacity: 1,
                                }}
                              />
                            </IconButton>
                          )}
                      </Box>
                      </TableCell>
                    </TableRow>
                    {expandedHole === n && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ backgroundColor: '#f9f9f9', p: 0 }}>
                          <Accordion sx={{ boxShadow: 'none', border: 0 }}>
                          <AccordionDetails sx={{ p: 2,  paddingLeft: 0, pr: 0  }}>
                              <Timeline position="right" sx={{ pl: 0, pr: 0 }}>
                              {[...timeline[String(n)]].reverse().map((entry, i, arr) => (
                                  <TimelineItem key={i}>
                                    <TimelineOppositeContent
                                      sx={{ flex: 0.15, fontSize: '0.75rem', color: 'text.secondary', alignSelf: 'center'}}
                                    >
                                      {new Date(entry.createdAt).toLocaleDateString()}
                                    </TimelineOppositeContent>

                                    <TimelineSeparator>
                                      <TimelineDot
                                        className={`bk-${entry.battleKey} dot`}
                                        sx={{
                                          width: 28,
                                          height: 28,
                                          backgroundImage: `url(/avatars/${entry.battleKey}.png)`,
                                          backgroundSize: 'cover',
                                          backgroundPosition: 'center',
                                          border: '2px solid white',
                                        }}
                                      />
                                      {i < arr.length - 1 && <TimelineConnector />}
                                    </TimelineSeparator>

                                    <TimelineContent sx={{overflow: 'hidden'}}>
                                      <Typography variant="body2" fontWeight={600} sx={{overflow:'hidden', textOverflow:'ellipsis'}}>
                                        {entry.battleKey}
                                      </Typography>
                                      <Typography variant="body2">Scored {entry.score}</Typography>
                                    </TimelineContent>
                                  </TimelineItem>
                                ))}
                              </Timeline>
                            </AccordionDetails>
                          </Accordion>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 4 }}>
            <Button onClick={onShowScoreForm} size="small" variant="contained">Claim Territory</Button>
          </Box>
          </Paper>
          <Paper elevation={0} sx={{background: 'transparent', border: '5px solid transparent'}}>
          <Typography variant="h2" sx={{mb: 3, mt: 4}}>
            Rules
          </Typography>
          {rules.map((subtitle, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', minHeight:'85px', mb: 3 }}>
            <Typography
              variant="h2"
              onClick={() => onRuleTap?.(String(i + 1))}
              sx={{
                mr: 3,
                fontWeight: 700,
                lineHeight: 1,
                fontFamily: 'Inter',
                minWidth: '40px',
                textAlign: 'center',
                borderRadius: '50%',
                border: '5px solid #106a41',
                color: '#106a41',
                height: '60px',
                width: '60px',
                padding: '7px',
                flexShrink: '0',
                cursor: 'pointer'
              }}
            >
                {i + 1}
              </Typography>
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    fontFamily: 'Inter',
                    textTransform: 'uppercase',
                    fontSize: {
                      xs: '1rem',   // mobile
                      sm: '0.875rem', // tablet
                      md: '1rem',     // default
                    },
                  }}
                >
                  {ruleTitles[i]}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              </Box>
            </Box>
          ))}
          </Paper>
          <Paper elevation={0} sx={{background: 'transparent', border: '5px solid transparent'}}>
          <Typography variant="h2" sx={{mb: 3, mt: 2}}>
            Prizes
          </Typography>
          {prizes.map((subtitle, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', minHeight:'85px', mb: 3 }}>
            <Typography
              variant="h2"
              sx={{
                mr: 3,
                fontWeight: 700,
                lineHeight: 1,
                fontFamily: 'Inter',
                minWidth: '40px',
                textAlign: 'center',
                borderRadius: '50%',
                border: '5px solid #106a41',
                color: '#106a41',
                height: '60px',
                width: '60px',
                padding: '7px',
                flexShrink: '0'
              }}
            >
                {i + 1}
              </Typography>
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    fontFamily: 'Inter',
                    textTransform: 'uppercase',
                    fontSize: {
                      xs: '1rem',   // mobile
                      sm: '0.875rem', // tablet
                      md: '1rem',     // default
                    },
                  }}
                >
                  {prizeTitles[i]}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              </Box>
            </Box>
          ))}
          </Paper>
      </Grid>
    </Grid>
  );
}
